import { useRef } from 'react';
import { T } from 'gt-react';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

/**
 * Picking a workbook, and what the upload is doing.
 *
 * `progress` is null until the transfer starts and again once it finishes —
 * the gap between the two is the server reading the workbook, which is the
 * slow part and needs saying, because a bare full progress bar looks stuck.
 */
export function MasterlistUploadPanel({
  hasMasterlist,
  isUploading,
  progress,
  onPick,
}: {
  hasMasterlist: boolean;
  isUploading: boolean;
  progress: number | null;
  onPick: (file: File) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so picking the same file twice in a row still fires a change.
    event.target.value = '';
    if (file) onPick(file);
  }

  return (
    <div className="mt-3 space-y-3">
      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleChange}
      />
      <Button onClick={() => fileInput.current?.click()} disabled={isUploading}>
        <UploadSimpleIcon className="mr-2 h-4 w-4" />
        {hasMasterlist ? (
          <T context="PCSC Masterlist page, upload button">
            Replace Masterlist
          </T>
        ) : (
          <T context="PCSC Masterlist page, upload button">Upload Masterlist</T>
        )}
      </Button>
      {progress !== null ? <Progress value={progress} /> : null}
      {isUploading && progress === null ? (
        <p className="text-muted-foreground text-sm">
          <T context="PCSC Masterlist page, upload progress">
            Reading the workbook…
          </T>
        </p>
      ) : null}
      <p className="text-muted-foreground text-xs">
        <T context="PCSC Masterlist page, upload caveat">
          Uploading replaces the masterlist in force. Every SPA record resolves
          against the new workbook straight away.
        </T>
      </p>
    </div>
  );
}
